/**
 * Permission store — caches the current user's permissions from the API.
 * Drives module launcher visibility and sidebar feature filtering.
 */
import { create } from 'zustand';
import api from '../api/client';

interface PermissionStore {
  permissions: Set<string>;
  roles: string[];
  modules: string[];
  isSuperuser: boolean;
  isTenantAdmin: boolean;
  crossBranch: boolean;
  loaded: boolean;
  loading: boolean;
  load: () => Promise<void>;
  can: (module: string, action: string) => boolean;
  canAccessModule: (moduleId: string) => boolean;
  reset: () => void;
}

export const usePermissionStore = create<PermissionStore>((set, get) => ({
  permissions: new Set(),
  roles: [],
  modules: [],
  isSuperuser: false,
  isTenantAdmin: false,
  crossBranch: false,
  loaded: false,
  loading: false,

  load: async () => {
    if (get().loading) return;
    set({ loading: true });
    try {
      const r = await api.get('/admin/rbac/my-permissions');
      set({
        permissions: new Set(r.data.permissions || []),
        roles: r.data.roles || [],
        modules: r.data.modules || [],
        isSuperuser: r.data.is_superuser || false,
        isTenantAdmin: r.data.is_tenant_admin || false,
        crossBranch: r.data.cross_branch || false,
        loaded: true,
      });
    } catch {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.is_superuser) {
        set({ isSuperuser: true, isTenantAdmin: true, modules: ['*'], crossBranch: true, loaded: true });
      }
    } finally {
      set({ loading: false });
    }
  },

  can: (module: string, action: string): boolean => {
    const { isSuperuser, isTenantAdmin, permissions, loaded } = get();
    if (!loaded) return true;
    if (isSuperuser || isTenantAdmin) return true;
    return permissions.has(`${module}:${action}`);
  },

  canAccessModule: (moduleId: string): boolean => {
    const { isSuperuser, isTenantAdmin, modules, loaded } = get();
    if (!loaded) return true;
    if (isSuperuser || isTenantAdmin || modules.includes('*')) return true;
    if (moduleId === 'myspace') return true;
    const legacy: Record<string, string> = { procurement: 'supplychain', inventory: 'supplychain', dealer: 'supplychain' };
    const resolved = legacy[moduleId] || moduleId;
    return modules.includes(resolved) || modules.includes(moduleId);
  },

  reset: () => set({
    permissions: new Set(), roles: [], modules: [],
    isSuperuser: false, isTenantAdmin: false, crossBranch: false, loaded: false,
  }),
}));
